/**
 * ls-calculate-yard-size.js  (same-page address + map edition)
 *
 * MAP     : Google Maps (satellite, rotate, tilt, map-type controls)
 * DRAWING : Terra Draw — LineString + Select
 * TOOLBAR : Select | Polygon | Undo | Delete
 *
 * theme.liquid scripts needed:
 *   <script src="https://maps.googleapis.com/maps/api/js?key=YOUR_KEY&libraries=places,geometry&v=weekly&callback=Function.prototype" defer></script>
 *   <script src="https://unpkg.com/terra-draw@1.0.0/dist/terra-draw.umd.js"></script>
 *   <script src="https://unpkg.com/terra-draw-google-maps-adapter@1.0.2/dist/terra-draw-google-maps-adapter.umd.js"></script>
 */

/* ═══════════════════════════════════════════════════════════════════════════
   ADDRESS AUTOCOMPLETE FIELD
═══════════════════════════════════════════════════════════════════════════ */
customElements.get('address-autocomplete-field') || customElements.define('address-autocomplete-field',
    class extends HTMLElement {
        constructor() { super() }

        connectedCallback() {
            this.initAutocomplete()

            const submitBtn = this.querySelector('.address-autocomplete-field__submit-button')
            if (submitBtn) {
                submitBtn.addEventListener('click', () => this.handleButtonClick())
            }

            this.querySelector('input')?.addEventListener('focus', () => this.handleRemoveErrorMessage())
        }

        initAutocomplete() {
            const input = this.querySelector('#autocomplete')
            // Skip the read-only display input
            if (!input || input.disabled) return

            const autocomplete = new google.maps.places.Autocomplete(input, {
                types: ['street_address', 'premise'],
                componentRestrictions: { country: ['US'] },
                fields: ['geometry', 'formatted_address']
            })

            autocomplete.addListener('place_changed', () => this.onPlaceChanged(autocomplete))
        }

        showLoader() {
            this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden')
            this.querySelector('button')?.classList.add('ls-button--text-hidden')
        }

        hideLoader() {
            this.querySelector('.loading-overlay__spinner')?.classList.add('hidden')
            this.querySelector('button')?.classList.remove('ls-button--text-hidden')
        }

        onPlaceChanged = (autocomplete) => {
            const place = autocomplete.getPlace()

            if (!place.geometry) {
                const input = this.querySelector('#autocomplete')
                if (input) input.placeholder = 'Enter your home address'
                return
            }

            const lat = place.geometry.location.lat()
            const lng = place.geometry.location.lng()
            const address = place.formatted_address

            sessionStorage.setItem('ls-lat', lat)
            sessionStorage.setItem('ls-lng', lng)
            sessionStorage.setItem('ls-address', address)

            // Notify calculate-yard-size on the same page
            document.dispatchEvent(new CustomEvent('placeChanged', {
                detail: { lat, lng, address }
            }))
        }

        handleRemoveErrorMessage() {
            this.querySelector('#address-autocomplete-field__error-message')?.remove()
        }

        handleErrorMessage() {
            if (this.querySelector('#address-autocomplete-field__error-message')) return
            const div = document.createElement('div')
            div.id = 'address-autocomplete-field__error-message'
            div.textContent = 'Please type your address in the field below and select an address from the dropdown. 👇'
            this.insertAdjacentElement('afterbegin', div)
        }

        handleButtonClick() {
            this.showLoader()

            if (sessionStorage.getItem('ls-lat')) {
                // Coords already stored (user picked from dropdown) — re-fire event
                document.dispatchEvent(new CustomEvent('placeChanged', {
                    detail: {
                        lat: parseFloat(sessionStorage.getItem('ls-lat')),
                        lng: parseFloat(sessionStorage.getItem('ls-lng')),
                        address: sessionStorage.getItem('ls-address')
                    }
                }))
                this.hideLoader()
            } else {
                this.hideLoader()
                this.handleErrorMessage()
            }
        }
    }
)

/* ═══════════════════════════════════════════════════════════════════════════
   CALCULATE YARD SIZE
═══════════════════════════════════════════════════════════════════════════ */

customElements.get('calculate-yard-size') || customElements.define(
    'calculate-yard-size',
    class extends HTMLElement {

        constructor() {
            super()
            this.map = null
            this.polygons = []
            this.infoWindows = []
            this.lat = Number(sessionStorage.getItem('ls-lat'))
            this.lng = Number(sessionStorage.getItem('ls-lng'))
            this.totalArea = 0
            this.draw = null
            this.tdReady = false
            this.gmPolygons = new Map()
            this.drawingPolylines = []
            this._addressMarker = null

            this.dialog = this.querySelector('.ls-modal__dialog')
            this.closeX = this.querySelector('.ls-modal__close-x')
        }

        /* ─────────────────────────────────────────────────────────────────
           LIFECYCLE
        ───────────────────────────────────────────────────────────────── */

        connectedCallback() {
            // Try to init map immediately if coords are already in session
            this.initMap()
            this.initAddress()
            this.initHandleButtonClicks()
            this.handleAddressChangeClick()

            // ✅ KEY CHANGE: on this template placeChanged centres the map
            //    instead of redirecting to another page.
            document.addEventListener('placeChanged', (e) => {
                if (e.detail?.lat && e.detail?.lng) {
                    this.lat = e.detail.lat
                    this.lng = e.detail.lng
                    sessionStorage.setItem('ls-lat', this.lat)
                    sessionStorage.setItem('ls-lng', this.lng)
                    this._recenterMap(e.detail.lat, e.detail.lng, e.detail.address)
                }
            }, false)

            this.handleCloseClick()
            this.handleBackdropClick()
        }

        disconnectedCallback() { }

        _mapEl() { return this.querySelector('#property-map-find-yarn') }

        /* ─────────────────────────────────────────────────────────────────
           MAP INIT
        ───────────────────────────────────────────────────────────────── */

        initMap() {
            // ✅ KEY CHANGE: guard — map initialises only when coords are available.
            // Default to USA center if no coords
            if (!this.lat || !this.lng) {
                this.lat = 37.6000
                this.lng = -95.6650
            }

            this.map = new google.maps.Map(this._mapEl(), {
                zoom: this.lat === 37.6000 ? 4 : 20,
                center: { lat: this.lat, lng: this.lng },
                gestureHandling: 'greedy',
                mapTypeId: 'satellite',
                mapTypeControl: false,
                mapTypeControlOptions: {
                    style: google.maps.MapTypeControlStyle.DEFAULT,
                    position: google.maps.ControlPosition.BOTTOM_RIGHT
                },
                rotateControl: true,
                fullscreenControl: true,
                streetViewControl: false,
                tilt: 0,
                zoomControl: true,
                zoomControlOptions: { position: google.maps.ControlPosition.TOP_LEFT },
                backgroundColor: 'transparent'
            })

            // ✅ Store marker reference so _recenterMap() can replace it
            this._addressMarker = new google.maps.Marker({
                position: { lat: this.lat, lng: this.lng },
                map: this.map,
                title: 'Entered Address',
                icon: { url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png' }
            })

            google.maps.event.addListenerOnce(this.map, 'idle', () => this.initTerraDraw())
        }

        // ✅ NEW: centres an already-initialised map, or bootstraps it for the first time
        _recenterMap(lat, lng, address) {
            // Update the address display input
            const f = this.querySelector('.address-display #autocomplete')
            if (f && address) f.value = address

            if (!this.map) {
                // First search — initialise the map now
                this.initMap()
                return
            }

            // Map already exists — just re-centre and drop a new pin
            this.map.setCenter({ lat, lng })
            this.map.setZoom(20)

            if (this._addressMarker) this._addressMarker.setMap(null)
            this._addressMarker = new google.maps.Marker({
                position: { lat, lng },
                map: this.map,
                title: 'Entered Address',
                icon: { url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png' }
            })
        }

        initAddress() {
            // ✅ KEY CHANGE: no redirect — address field lives on this page.
            //    Simply pre-fill the display input from session if returning.
            const address = sessionStorage.getItem('ls-address')
            const f = this.querySelector('.address-display #autocomplete')
            if (f && address) f.value = address
        }

        /* ─────────────────────────────────────────────────────────────────
           TERRA DRAW INIT
        ───────────────────────────────────────────────────────────────── */

        initTerraDraw() {
            if (this.tdReady) return
            const td = window.terraDraw
            const adp = window.terraDrawGoogleMapsAdapter
            if (!td || !adp) { console.error('Terra Draw scripts missing!'); return }

            const { TerraDraw, TerraDrawPolygonMode } = td
            const { TerraDrawGoogleMapsAdapter } = adp
            this.tdReady = true

            const pointerDistance = ('ontouchstart' in window || navigator.maxTouchPoints > 0) ? 7 : 2

            const adapter = new TerraDrawGoogleMapsAdapter({
                lib: google.maps,
                map: this.map,
                coordinatePrecision: 9
            });

            // Monkey-patch: Google Maps Data Layer events contain .latLng which is perfectly accurate.
            // Using e.latLng bypasses the adapter's faulty clientX/clientY screen coordinate conversion during fullscreen.
            const coordPrecision = 9;
            adapter.getLngLatFromEvent = function (e) {
                if (e && e.latLng) {
                    return {
                        lng: parseFloat(e.latLng.lng().toFixed(coordPrecision)),
                        lat: parseFloat(e.latLng.lat().toFixed(coordPrecision))
                    };
                }

                // For standard DOM pointer events, we MUST use the adapter's original method 
                // because it accounts for Google Maps' internal pane panning transformations.
                if (!this._overlay) return null;
                var bounds = this._map.getBounds();
                if (!bounds) return null;

                // Use the ORIGINAL getMapEventElement() which respects the panned pane bounds
                var rect = this.getMapEventElement().getBoundingClientRect();
                var x = e.clientX - rect.left;
                var y = e.clientY - rect.top;

                var point = new this._lib.Point(x, y);
                var proj = this._overlay.getProjection();
                if (!proj) return null;

                var ne = bounds.getNorthEast(), sw = bounds.getSouthWest();
                var latlngBounds = new this._lib.LatLngBounds(sw, ne);
                var latLng = proj.fromContainerPixelToLatLng(point);

                if (!latLng || !latlngBounds.contains(latLng)) return null;

                return {
                    lng: parseFloat(latLng.lng().toFixed(coordPrecision)),
                    lat: parseFloat(latLng.lat().toFixed(coordPrecision))
                };
            };

            // Fix Terra Draw canvas resizing in fullscreen:
            // Terra Draw's internal canvas only resizes on window resize events.
            // When Google Maps goes fullscreen, it doesn't always trigger a window resize,
            // leaving the canvas stretched and offset. We manually trigger map and window resizes.
            if (!this._fullscreenListenerAdded) {
                this._fullscreenListenerAdded = true;
                document.addEventListener('fullscreenchange', () => {
                    setTimeout(() => {
                        google.maps.event.trigger(this.map, "resize");
                        window.dispatchEvent(new Event('resize'));
                    }, 100);
                });
            }

            this.draw = new TerraDraw({
                adapter: adapter,
                modes: [
                    new TerraDrawPolygonMode({
                        allowSelfIntersections: true,
                        pointerDistance,
                        styles: {
                            fillColor: '#FFE512',
                            fillOpacity: 0,
                            outlineColor: 'transparent',
                            outlineWidth: 0,
                            closingPointColor: '#FFE512',
                            closingPointWidth: 4,
                            closingPointOutlineColor: '#ffffff',
                            closingPointOutlineWidth: 1
                        }
                    })
                ]
            })

            this.draw.start()
            this.draw.on('ready', () => {
                this.draw.setMode('polygon')
                this.injectToolbar()
            })
            this.draw.on('change', (ids, type) => this.onFeatureChange(ids, type))
            this.draw.on('finish', (featureId) => this.onPolygonFinished(featureId))
        }

        /* ─────────────────────────────────────────────────────────────────
           TOOLBAR
        ───────────────────────────────────────────────────────────────── */

        injectToolbar() {
            if (document.getElementById('ls-td-toolbar')) return
            if (!document.getElementById('ls-tb-style')) {
                const s = document.createElement('style')
                s.id = 'ls-tb-style'
                s.textContent = `
                  .ls-toolbar{display:flex;align-items:stretch;background:#fff;border-radius:8px;box-shadow:0 2px 10px rgba(0,0,0,.35);font-family:Roboto,Arial,sans-serif;margin:10px 0;overflow:hidden;}
                  .ls-tb-btn{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;min-width:64px;padding:10px 12px 8px;border:none;border-right:1px solid #e8eaed;border-bottom:3px solid transparent;background:transparent;cursor:pointer;font-size:11px;font-weight:500;color:#5f6368;white-space:nowrap;transition:background .12s,color .12s;line-height:1.2;}
                  .ls-tb-btn:last-child{border-right:none}
                  .ls-tb-btn:hover{background:#f1f3f4;color:#202124}
                  .ls-tb-btn.is-active{background:#e8f0fe;color:#1967d2;border-bottom-color:#1967d2}
                `
                document.head.appendChild(s)
            }

            const wrap = document.createElement('div')
            wrap.id = 'ls-td-toolbar'
            wrap.className = 'ls-toolbar'

            const selBtn = document.createElement('button')
            selBtn.type = 'button'
            selBtn.className = 'ls-tb-btn'
            selBtn.title = 'Select a shape to move or edit vertices'
            selBtn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M5 3l5.5 14 2-6.5L19 8.5Z" fill="currentColor"/><line x1="14.5" y1="14.5" x2="21" y2="21" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg><span>Select</span>`

            const drawBtn = document.createElement('button')
            drawBtn.type = 'button'
            drawBtn.className = 'ls-tb-btn is-active'
            drawBtn.title = 'Click to place points — click the first point to close'
            drawBtn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><polygon points="12,3 22,18 2,18" stroke="currentColor" stroke-width="2" fill="currentColor" fill-opacity=".2"/><circle cx="12" cy="3" r="2.5" fill="currentColor"/><circle cx="22" cy="18" r="2.5" fill="currentColor"/><circle cx="2" cy="18" r="2.5" fill="currentColor"/></svg><span>Polygon</span>`

            const setActive = (mode) => {
                if (mode === 'polygon') {
                    drawBtn.classList.add('is-active'); selBtn.classList.remove('is-active')
                } else {
                    selBtn.classList.add('is-active'); drawBtn.classList.remove('is-active')
                }
            }

            selBtn.addEventListener('click', () => { this._enableEditing(); setActive('select') })
            drawBtn.addEventListener('click', () => { this._disableEditing(); setActive('polygon') })

            wrap.appendChild(selBtn)
            wrap.appendChild(drawBtn)
            this.map.controls[google.maps.ControlPosition.TOP_LEFT].push(wrap)
        }

        /* ─────────────────────────────────────────────────────────────────
           FEATURE CHANGE / FINISH
        ───────────────────────────────────────────────────────────────── */

        onFeatureChange(ids, type) {
            // Ignore internal Terra Draw deletes. We handle our own polygon lifecycle and
            // only delete when the user explicitly clicks the delete button in the InfoWindow.
            // This prevents polygons from disappearing when Terra Draw restarts/clears state.
            if (type === 'delete') {
                return;
            }
            const snapshot = this.draw.getSnapshot()
            ids.forEach((featureId) => {
                const feat = snapshot.find((f) => f.id === featureId)
                if (!feat || feat.geometry.type !== 'Polygon') return
                const ring = feat.geometry.coordinates[0]
                if (!ring || ring.length < 4) return
                const area = this.calculateSqFt(ring)
                const center = this.computeCenter(ring)
                const existing = this.polygons.find((p) => p.featureId === featureId)
                if (existing) {
                    existing.area = area
                    existing.center = center
                    this.updateInfoWindow(featureId, area, center)
                    this._updateGmPolygon(featureId, ring)
                }
                if (!this.polygons.find((p) => p.featureId === featureId)) {
                    this._renderDrawingEdges(ring)
                }
            })
            this.updateYardAreas()
        }

        onPolygonFinished(featureId) {
            this._clearDrawingEdges()
            const snapshot = this.draw.getSnapshot()
            const feat = snapshot.find((f) => f.id === featureId)
            if (!feat || feat.geometry.type !== 'Polygon') return
            const ring = feat.geometry.coordinates[0]
            if (!ring || ring.length < 4) return
            const area = this.calculateSqFt(ring)
            const center = this.computeCenter(ring)
            if (!this.polygons.find((p) => p.featureId === featureId)) {
                this.polygons.push({ featureId, area, center, ring })
                this.displayInfoWindow(featureId, area, center)
                this._createGmPolygonOverlay(featureId, ring)
            }
            this.updateYardAreas()
        }

        /* ─────────────────────────────────────────────────────────────────
           GM POLYGON OVERLAYS
        ───────────────────────────────────────────────────────────────── */

        _createGmPolygonOverlay(featureId, ring) {
            const polygon = new google.maps.Polygon({
                paths: ring.map(c => ({ lat: c[1], lng: c[0] })),
                fillColor: '#FFE512', fillOpacity: 0.3,
                strokeColor: '#FFE512', strokeOpacity: 1, strokeWeight: 2,
                map: this.map, clickable: false
            })
            const dots = []
            for (let i = 0; i < ring.length - 1; i++) {
                dots.push(this._createDot(ring[i][1], ring[i][0]))
                const midLat = (ring[i][1] + ring[i + 1][1]) / 2
                const midLng = (ring[i][0] + ring[i + 1][0]) / 2
                dots.push(this._createDot(midLat, midLng, 8, 'rgba(255,229,18,0.6)', '#ccc'))
            }
            this.gmPolygons.set(featureId, { polygon, dots })
        }

        _enableEditing() {
            this.draw.stop()
            this.gmPolygons.forEach((entry, featureId) => {
                entry.dots.forEach((d) => d.setMap(null))
                entry.polygon.setOptions({ editable: true, draggable: true, clickable: true })
                const path = entry.polygon.getPath()
                const onEdit = () => this._onGmPathEdit(featureId)
                entry.listeners = [
                    google.maps.event.addListener(path, 'set_at', onEdit),
                    google.maps.event.addListener(path, 'insert_at', onEdit),
                    google.maps.event.addListener(path, 'remove_at', onEdit),
                    google.maps.event.addListener(entry.polygon, 'dragend', onEdit)
                ]
            })
        }

        _disableEditing() {
            this.gmPolygons.forEach((entry) => {
                if (entry.listeners) {
                    entry.listeners.forEach((l) => google.maps.event.removeListener(l))
                    entry.listeners = null
                }
                entry.polygon.setOptions({ editable: false, draggable: false, clickable: false })
                const path = entry.polygon.getPath()
                const ring = []
                path.forEach((p) => ring.push([p.lng(), p.lat()]))
                ring.push(ring[0])
                const newDots = []
                for (let i = 0; i < ring.length - 1; i++) {
                    newDots.push(this._createDot(ring[i][1], ring[i][0]))
                    const midLat = (ring[i][1] + ring[i + 1][1]) / 2
                    const midLng = (ring[i][0] + ring[i + 1][0]) / 2
                    newDots.push(this._createDot(midLat, midLng, 8, 'rgba(255,229,18,0.6)', '#ccc'))
                }
                entry.dots = newDots
            })
            this.draw.start()
            this.draw.setMode('polygon')
        }

        _onGmPathEdit(featureId) {
            const entry = this.gmPolygons.get(featureId)
            if (!entry) return
            const path = entry.polygon.getPath()
            const ring = []
            path.forEach((p) => ring.push([p.lng(), p.lat()]))
            ring.push(ring[0])
            const area = this.calculateSqFt(ring)
            const center = this.computeCenter(ring)
            const existing = this.polygons.find((p) => p.featureId === featureId)
            if (existing) { existing.area = area; existing.center = center; existing.ring = ring; this.updateInfoWindow(featureId, area, center) }
            this.updateYardAreas()
        }

        _updateGmPolygon(featureId, ring) {
            const entry = this.gmPolygons.get(featureId)
            if (!entry) return
            entry.polygon.setPaths(ring.map(c => ({ lat: c[1], lng: c[0] })))
            entry.dots.forEach((d) => d.setMap(null))
            const newDots = []
            for (let i = 0; i < ring.length - 1; i++) {
                newDots.push(this._createDot(ring[i][1], ring[i][0]))
                const midLat = (ring[i][1] + ring[i + 1][1]) / 2
                const midLng = (ring[i][0] + ring[i + 1][0]) / 2
                newDots.push(this._createDot(midLat, midLng, 8, 'rgba(255,229,18,0.6)', '#ccc'))
            }
            entry.dots = newDots
        }

        _createDot(lat, lng, size, bg, border) {
            const s = size || 10, b = bg || '#FFE512', br = border || '#fff'
            const overlay = new google.maps.OverlayView()
            overlay.position = new google.maps.LatLng(lat, lng)
            overlay.onAdd = function () {
                this.div = document.createElement('div')
                this.div.style.cssText = `position:absolute;width:${s}px;height:${s}px;border-radius:50%;background:${b};border:1.5px solid ${br};pointer-events:none;transform:translate(-50%,-50%);`
                this.getPanes().markerLayer.appendChild(this.div)
            }
            overlay.draw = function () {
                const pos = this.getProjection().fromLatLngToDivPixel(this.position)
                if (this.div) { this.div.style.left = pos.x + 'px'; this.div.style.top = pos.y + 'px' }
            }
            overlay.onRemove = function () { if (this.div) { this.div.parentNode.removeChild(this.div); this.div = null } }
            overlay.setMap(this.map)
            return overlay
        }

        /* ─────────────────────────────────────────────────────────────────
           DRAWING EDGES
        ───────────────────────────────────────────────────────────────── */

        _renderDrawingEdges(ring) {
            this._clearDrawingEdges()
            for (let i = 0; i < ring.length - 2; i++) {
                const line = new google.maps.Polyline({
                    path: [{ lat: ring[i][1], lng: ring[i][0] }, { lat: ring[i + 1][1], lng: ring[i + 1][0] }],
                    strokeColor: '#FFE512', strokeOpacity: 1, strokeWeight: 2, map: this.map, clickable: false
                })
                this.drawingPolylines.push(line)
            }
            for (let i = 0; i < ring.length - 1; i++) {
                this.drawingPolylines.push(this._createDot(ring[i][1], ring[i][0]))
                if (i < ring.length - 2) {
                    const midLat = (ring[i][1] + ring[i + 1][1]) / 2
                    const midLng = (ring[i][0] + ring[i + 1][0]) / 2
                    this.drawingPolylines.push(this._createDot(midLat, midLng, 8, 'rgba(255,229,18,0.6)', '#ccc'))
                }
            }
        }

        _clearDrawingEdges() {
            this.drawingPolylines.forEach((line) => line.setMap(null))
            this.drawingPolylines = []
        }

        /* ─────────────────────────────────────────────────────────────────
           GEOMETRY HELPERS
        ───────────────────────────────────────────────────────────────── */

        calculateSqFt(ring) {
            if (!window.google || !google.maps.geometry) return 0
            const path = ring.map(coord => new google.maps.LatLng(coord[1], coord[0]))
            return Math.round(google.maps.geometry.spherical.computeArea(path) * 10.7639)
        }

        computeCenter(ring) {
            const pts = ring.slice(0, -1)
            return {
                lat: pts.reduce((s, p) => s + p[1], 0) / pts.length,
                lng: pts.reduce((s, p) => s + p[0], 0) / pts.length
            }
        }

        /* ─────────────────────────────────────────────────────────────────
           INFO WINDOW
        ───────────────────────────────────────────────────────────────── */

        _iwContent(featureId, area) {
            return `
        <div class="ls-iw-compact" style="display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 500; color: #5f6368; padding: 2px 4px;">
          <span>${Intl.NumberFormat('en-US').format(area)} sqft</span>
          <button id="ls-del-${featureId}" type="button" 
            style="background:none;border:none;cursor:pointer;padding:0;display:flex;align-items:center;margin-left:auto;">
            <svg style="width:12px;height:12px;" viewBox="0 0 448 512"><path fill="#d00" d="M432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16zM53.2 467a48 48 0 0 0 47.9 45h245.8a48 48 0 0 0 47.9-45L416 128H32z"/></svg>
          </button>
        </div>`;
        }

        displayInfoWindow(featureId, area, center) {
            const iw = new google.maps.InfoWindow({
                content: this._iwContent(featureId, area),
                position: center,
                disableAutoPan: true,
                headerDisabled: true
            })
            iw.open({ map: this.map, shouldFocus: false })
            this.infoWindows.push({ featureId, iw })
            google.maps.event.addListener(iw, 'domready', () => this._bindDeleteBtn(featureId))
        }

        updateInfoWindow(featureId, area, center) {
            const entry = this.infoWindows.find((e) => e.featureId === featureId)
            if (!entry) return
            entry.iw.setContent(this._iwContent(featureId, area))
            entry.iw.setPosition(center)
            google.maps.event.addListener(entry.iw, 'domready', () => this._bindDeleteBtn(featureId))
        }

        _bindDeleteBtn(featureId) {
            const btn = document.getElementById(`ls-del-${featureId}`)
            if (!btn) return
            btn.onclick = (e) => {
                e.stopPropagation()
                this.removePolygon(featureId, true)
                this.updateYardAreas()
            }
        }

        removePolygon(featureId, alsoRemoveFromDraw = false) {
            if (alsoRemoveFromDraw && this.draw) {
                try { this.draw.setMode('static'); this.draw.removeFeatures([featureId]); this.draw.setMode('polygon') } catch (e) { console.error(e) }
            }
            const gmEntry = this.gmPolygons.get(featureId)
            if (gmEntry) {
                if (gmEntry.polygon) gmEntry.polygon.setMap(null)
                if (gmEntry.dots) gmEntry.dots.forEach((d) => d.setMap(null))
                this.gmPolygons.delete(featureId)
            }
            this._clearDrawingEdges()
            this.polygons = this.polygons.filter((p) => p.featureId !== featureId)
            const entry = this.infoWindows.find((e) => e.featureId === featureId)
            if (entry) { entry.iw.close(); this.infoWindows = this.infoWindows.filter((e) => e.featureId !== featureId) }
        }

        /* ─────────────────────────────────────────────────────────────────
           AREA / STORAGE
        ───────────────────────────────────────────────────────────────── */

        updateYardAreas() {
            this.totalArea = this.polygons.reduce((a, c) => a + Number(c.area || 0), 0)
            sessionStorage.setItem('ls-yard-area', this.totalArea)
            this.addSquareFootageText(this.totalArea)
            this.updateProductEstimates(this.totalArea)
        }

        addSquareFootageText(value) {
            const fields = document.querySelectorAll('#calculate-yard-size__total-area')
            if (!fields.length) return
            const n = Number(String(value).replace(/[^0-9.]/g, '')) || 0
            const text = n ? `${Intl.NumberFormat('en-US').format(n)} sq. ft.` : '0 sq. ft.'
            fields.forEach(field => { field.value = text })
        }

        /* ─────────────────────────────────────────────────────────────────
           BUTTON HANDLERS
        ───────────────────────────────────────────────────────────────── */

        initHandleButtonClicks() {
            this.querySelector('#calculate-yard-size__update-area')
                ?.addEventListener('click', this.handleUpdateAreaButtonClick)
            this.querySelector('#calculate-yard-size__update-area-cancel')
                ?.addEventListener('click', this.handleCancelAreaButtonClick)
            this.querySelector('#calculate-yard-size__update-area-save')
                ?.addEventListener('click', this.handleSaveAreaButtonClick)
            this.querySelector('#calculate-yard-size__next-page')
                ?.addEventListener('click', this.handleNextButtonClick)
        }

        handleUpdateAreaButtonClick = () => {
            // Clear all polygons
            ;[...this.polygons].forEach(p => this.removePolygon(p.featureId, true))
            this.infoWindows.forEach(e => e.iw?.close())
            this._clearDrawingEdges()
            this.polygons = []; this.infoWindows = []
            this.gmPolygons.forEach(entry => {
                if (entry.polygon) entry.polygon.setMap(null)
                if (entry.dots) entry.dots.forEach(d => d.setMap(null))
            })
            this.gmPolygons.clear()
            this.totalArea = 0; sessionStorage.setItem('ls-yard-area', 0)
            if (this.draw) { try { this.draw.stop() } catch (_) { } }
            this.tdReady = false
            const f = this.querySelector('#calculate-yard-size__total-area')
            if (f) { f.value = ''; f.placeholder = '0'; f.removeAttribute('disabled'); f.focus() }
            this.querySelector('#calculate-yard-size__update-area')
                ?.classList.add('calculate-yard-size__update-area--hidden')
            this.querySelector('.calculate-yard-size__update-wrapper')
                ?.classList.remove('calculate-yard-size__update-wrapper--hidden')
            this.updateProductEstimates(0)
        }

        handleSaveAreaButtonClick = () => {
            const f = this.querySelector('#calculate-yard-size__total-area')
            if (!f) return
            this.totalArea = Number(f.value.replace(/[^0-9.]/g, '')) || 0
            sessionStorage.setItem('ls-yard-area', this.totalArea)
            this.querySelector('#calculate-yard-size__update-area')
                ?.classList.remove('calculate-yard-size__update-area--hidden')
            f.setAttribute('disabled', 'disabled')
            this.querySelector('.calculate-yard-size__update-wrapper')
                ?.classList.add('calculate-yard-size__update-wrapper--hidden')
            this.addSquareFootageText(this.totalArea)
            this.updateProductEstimates(this.totalArea)
        }

        handleCancelAreaButtonClick = () => {
            if (this.draw) { try { this.draw.start(); this.draw.setMode('polygon') } catch (_) { } }
            this.querySelector('#calculate-yard-size__update-area')
                ?.classList.remove('calculate-yard-size__update-area--hidden')
            const f = this.querySelector('#calculate-yard-size__total-area')
            if (f) f.setAttribute('disabled', 'disabled')
            this.querySelector('.calculate-yard-size__update-wrapper')
                ?.classList.add('calculate-yard-size__update-wrapper--hidden')
            this.totalArea = 0; sessionStorage.setItem('ls-yard-area', 0)
            this.addSquareFootageText(0)
        }

        handleNextButtonClick = () => {
            const btn = this.querySelector('#calculate-yard-size__next-page')
            if (!btn) return
            btn.querySelector('span')?.classList.add('visibility-hidden')
            btn.querySelector('.loading-overlay__spinner')?.classList.remove('hidden')
            const area = Number(sessionStorage.getItem('ls-yard-area') || 0)
            if (area > 80000) {
                this.dialog.querySelector('.ls-calculate-yard-size__warning').innerHTML =
                    `<h3>Wow! You have a big yard.</h3>
           <p>Please contact us at <a class="ls-calculate-yard-size__warning-phone" href="tel:+19803171707">980-317-1707</a></p>`
                this.handleOpen()
            } else if (area > 0) {
                window.location.href = '/pages/choose-grass-type'
            } else {
                this.dialog.querySelector('.ls-calculate-yard-size__warning').innerHTML =
                    `<h3>Yard size must be greater than zero.</h3>
           <p>Use the Polygon tool to mark your lawn.</p>`
                this.handleOpen()
            }
            btn.querySelector('span')?.classList.remove('visibility-hidden')
            btn.querySelector('.loading-overlay__spinner')?.classList.add('hidden')
        }

        handleAddressChangeClick() {
            const btn = this.querySelector('#ls-calculate-yard-size__address-change-button')
            if (!btn) return
            btn.addEventListener('click', () => {
                // Clear the display input
                const displayInput = this.querySelector('.address-display #autocomplete')
                if (displayInput) displayInput.value = ''
                // Clear and focus the active search input
                const searchInput = this.querySelector('.search-row #autocomplete')
                if (searchInput) { searchInput.value = ''; searchInput.focus() }
            })
        }

        /* ─────────────────────────────────────────────────────────────────
           MODAL
        ───────────────────────────────────────────────────────────────── */

        handleOpen() { this.dialog.showModal(); this.dialog.scrollTo(0, 0) }

        handleClose() {
            this.dialog.classList.add('ls-modal__dialog--closing')
            setTimeout(() => {
                this.dialog.close()
                this.dialog.classList.remove('ls-modal__dialog--closing')
            }, 500)
        }

        handleCloseClick() {
            this.closeX?.addEventListener('click', () => this.handleClose())
        }

        handleBackdropClick() {
            this.dialog?.addEventListener('click', e => {
                const r = this.dialog.getBoundingClientRect()
                if (e.clientY < r.top || e.clientY > r.bottom ||
                    e.clientX < r.left || e.clientX > r.right) this.handleClose()
            })
        }

        updateProductEstimates(totalSqft) {
            const sqft = Number(totalSqft) || 0

            if (!sqft) {
                this._setProductValues(0, 0, 0)
                return
            }

            const base = sqft / 1000

            // Calculations (round UP)
            const fertilizerLow = Math.ceil(base * 4)
            const fertilizerHigh = Math.ceil(base * 5)

            const overseed = Math.ceil(base * 5)
            const newLawn = Math.ceil(base * 10)

            this._setProductValues(
                `${fertilizerLow}-${fertilizerHigh}`,
                overseed,
                newLawn
            )
        }
        _setProductValues(fertilizer, overseed, newLawn) {
            const fertEls = document.querySelectorAll('#fertilizer-range')
            const overEls = document.querySelectorAll('#overseed-amount')
            const newEls  = document.querySelectorAll('#newlawn-amount')

            fertEls.forEach(el => { el.textContent = `${fertilizer} lbs.` })
            overEls.forEach(el => { el.textContent = `${overseed} lbs.` })
            newEls.forEach(el  => { el.textContent = `${newLawn} lbs.` })
        }
    }
)